import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

function StatusBadge({ status, t }) {
  const cls = status === "connected" ? "good" : status === "coming_soon" ? "warn" : "";
  return (
    <span className={`tb-badge ${cls}`} data-testid={`int-status-${status}`}>
      {t(`integrations.status.${status}`)}
    </span>
  );
}

function IntegrationCard({ item, t }) {
  return (
    <article className="tb-int-card" data-testid={`int-${item.key}`}>
      <header className="tb-int-head">
        <strong>{item.name}</strong>
        <StatusBadge status={item.status} t={t} />
      </header>
      <p className="tb-int-purpose">{item.purpose}</p>
      <dl className="tb-int-meta">
        <div><dt>Type</dt><dd>{t(`integrations.type.${item.type}`)}</dd></div>
        <div><dt>Mode</dt><dd>{t(`integrations.mode.${item.mode}`)}</dd></div>
        <div><dt>Sync</dt><dd>{item.sync_frequency}</dd></div>
        <div><dt>Automation</dt><dd>L{item.automation_level}</dd></div>
        {item.source_of_truth_domains?.length > 0 && (
          <div className="tb-int-meta-wide">
            <dt>Domain</dt>
            <dd>{item.source_of_truth_domains.join(", ")}</dd>
          </div>
        )}
      </dl>
    </article>
  );
}

export default function Integrations() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const canView = user && (user.role === "admin" || user.features?.journal_enabled === true);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    api.get("/integrations")
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setErr(formatApiErrorDetail(e) || "Failed to load"))
      .finally(() => setLoading(false));
  }, [canView]);

  if (!canView) {
    return (
      <div className="tb-page">
        <h1>{t("integrations.title")}</h1>
        <div className="tb-card"><p className="tb-muted">{t("proposals.emptyForRole")}</p></div>
      </div>
    );
  }

  const connected = items.filter((i) => i.status === "connected");
  const coming = items.filter((i) => i.status !== "connected");

  return (
    <div className="tb-page" data-testid="integrations-page">
      <header className="tb-mem-header">
        <div>
          <h1>{t("integrations.title")}</h1>
          <p className="tb-muted">{t("integrations.subtitle")}</p>
        </div>
      </header>

      {err && <div className="tb-error">{err}</div>}
      {loading ? (
        <p className="tb-muted">{t("integrations.loading")}</p>
      ) : (
        <>
          <section className="tb-int-group">
            <h2>{t("integrations.groups.connected")} · {connected.length}</h2>
            <div className="tb-int-grid" data-testid="int-connected">
              {connected.map((it) => <IntegrationCard key={it.key} item={it} t={t} />)}
            </div>
          </section>
          <section className="tb-int-group">
            <h2>{t("integrations.groups.comingSoon")} · {coming.length}</h2>
            <div className="tb-int-grid tb-int-grid-dim" data-testid="int-coming">
              {coming.map((it) => <IntegrationCard key={it.key} item={it} t={t} />)}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
